import envConstants from "~/config/envConstants";
import {
  UnifyEventPortErrorV1,
  UnifyEventRobotErrorV1
} from "~/redux/warehouse/unify.openApi";
import { WorkstationSummaryDto } from "~/types/api";

export type UnifyErrorEvent = UnifyEventPortErrorV1 | UnifyEventRobotErrorV1;

export type UnifyErrorSource = "Port" | "Robot";

export const getUnifyErrorSource = (
  event: UnifyErrorEvent
): UnifyErrorSource => (event.case === "PortErrorV1" ? "Port" : "Robot");

// The fields the combined errors table renders, normalized across sources.
export type UnifyErrorRow = {
  timestamp: string;
  portId: number | null;
  binId: number | null;
  robotId: number | null;
  errorCode: number | null;
  errorDescription: string | null;
  status: string | null;
  source: UnifyErrorSource;
};

// Normalizes a port or robot error event into a single row shape.
export const toUnifyErrorRow = (event: UnifyErrorEvent): UnifyErrorRow => {
  if (event.case === "PortErrorV1") {
    const { item } = event;
    return {
      timestamp: item.timestamp,
      portId: item.portId,
      binId: null,
      robotId: null,
      errorCode: item.portErrorCode,
      errorDescription: item.portError,
      status: item.status ?? null,
      source: "Port"
    };
  }

  const { item } = event;
  return {
    timestamp: item.timestamp,
    portId: item.portId ?? null,
    binId: item.binId ?? null,
    robotId: item.robotId,
    errorCode: item.robotErrorCode ?? null,
    errorDescription: item.robotError ?? null,
    status: item.status ?? null,
    source: "Robot"
  };
};

// Unify reports some errors under a different name than the Status Lookup docs use,
// so the entry id built from the Unify name doesn't exist. Keyed by `${code}-${unifyName}`.
const statusLookupEntryIdOverrides: Record<string, string> = {
  "1400-VERSAPORT_DRIVER": "1400-MONOPORT_DRIVER"
};

export const getStatusLookupUrl = (
  errorCode: number | null | undefined,
  errorDescription: string | null | undefined
): string | null => {
  if (errorCode == null || !errorDescription) return null;
  const entryId = `${errorCode}-${errorDescription}`;
  return `${envConstants.STATUS_LOOKUP_BASE_URL}#${statusLookupEntryIdOverrides[entryId] ?? entryId}`;
};

export const getWorkstationPortIds = (
  workstation: WorkstationSummaryDto
): number[] => {
  const portIds = workstation.ports.map((port) => port.portId);
  const parentPortIds = workstation.ports
    .map((port) => port.parentPortId)
    .filter((parentPortId): parentPortId is number => parentPortId != null);

  return [...portIds, ...parentPortIds];
};

export const buildPortIdToWorkstationMap = (
  workstations: WorkstationSummaryDto[] | undefined,
  autostoreGridId: string | undefined
): Map<number, string> =>
  new Map(
    (workstations ?? [])
      .filter(
        (workstation) =>
          !autostoreGridId || workstation.autostoreGridId === autostoreGridId
      )
      .flatMap((workstation) =>
        getWorkstationPortIds(workstation).map((portId): [number, string] => [
          portId,
          workstation.deviceId
        ])
      )
  );

export const getWorkstationLabel = (
  portIdToWorkstation: Map<number, string>,
  portId: number | null | undefined
): string => {
  if (portId == null) return "";
  return portIdToWorkstation.get(portId) ?? "";
};
